/**
 * Health dashboard dialog: the overall ring, the component counts, the
 * subsystem status grid, the worst issues and the maintenance MTBF summary.
 * Scores follow the previous dashboard (green 100, yellow 70, red 40).
 */
import { useMemo } from 'react';
import { Dialog } from 'radix-ui';
import { HeartPulse, X } from 'lucide-react';
import { IconButton } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { useUI } from '@/state/store';
import { useTwin } from '../../store';
import { StatusIcon } from '../components/badges';
import { HealthRing } from '../components/bars';
import { EmptyState, SectionTitle, Stat } from '../components/layout';
import { useAssetName } from '../hooks/useAssetName';
import { useItemLookup } from '../hooks/useItemLookup';
import { useNowMs } from '../hooks/useNowMs';
import { collectIssues, pathTo } from '../lib/cardTree';
import { fmtDate } from '../lib/format';
import { bandFor, overallScore, scoreSubsystems } from '../lib/healthScore';
import { collectEvents, summariseHistory } from '../lib/maintenanceHistory';
import { useTwinUi } from '../state';

export function HealthDashboard() {
  const open = useTwinUi((s) => s.dialog === 'health');
  const closeDialog = useTwinUi((s) => s.closeDialog);
  const setTab = useTwinUi((s) => s.setTab);
  const setExpanded = useTwinUi((s) => s.setExpanded);
  const select = useUI((s) => s.select);
  const element = useTwin((s) => s.element);
  const name = useAssetName();
  const lookup = useItemLookup();
  const now = useNowMs(60_000);

  const components = useMemo(() => (element ? scoreSubsystems(element.subsystems, lookup.colourOf) : []), [element, lookup]);
  const issues = useMemo(() => (element ? collectIssues(element.subsystems, lookup.colourOf, lookup.valueOf) : []), [element, lookup]);
  const history = useMemo(() => (element ? summariseHistory(collectEvents(element), now) : null), [element, now]);
  const scores = components.map((c) => c.score);
  const overall = overallScore(scores);
  const band = bandFor(scores);
  const counts = {
    healthy: components.filter((c) => c.score >= 80).length,
    warning: components.filter((c) => c.score >= 60 && c.score < 80).length,
    critical: components.filter((c) => c.score < 60).length,
  };

  const goTo = (id: string) => {
    if (!element) return;
    setExpanded(pathTo(element.subsystems, id).map((s) => s.id), true);
    select({ subsystemId: id });
    setTab('health');
    closeDialog();
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && closeDialog()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content aria-describedby={undefined} className="fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-32px)] w-[min(820px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-panel border border-hairline-strong bg-raised text-xs shadow-elev-2 focus:outline-none">
          <div className="flex h-11 shrink-0 items-center justify-between border-b border-hairline px-4">
            <Dialog.Title className="flex items-center gap-2 text-sm font-semibold text-ink">
              <HeartPulse className="h-4 w-4 text-accent" /> System health dashboard
              {name && <span className="font-mono text-[11px] font-normal text-ink-3">{name.title}</span>}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton label="Close (Esc)">
                <X className="h-4 w-4" />
              </IconButton>
            </Dialog.Close>
          </div>
          {!element ? (
            <EmptyState title="No twin loaded." className="m-4" />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 scrollbar-thin">
              <div className="flex flex-wrap items-center gap-4">
                <HealthRing value={overall} label="Overall" colour={band === 'healthy' ? 'green' : band === 'warning' ? 'yellow' : 'red'} />
                <div className="grid flex-1 grid-cols-3 gap-2">
                  <Stat label="Healthy" value={counts.healthy} tone="nominal" />
                  <Stat label="Warning" value={counts.warning} tone={counts.warning ? 'caution' : undefined} />
                  <Stat label="Critical" value={counts.critical} tone={counts.critical ? 'warning' : undefined} />
                </div>
              </div>
              <div>
                <SectionTitle right={<span className="font-mono text-[10px] text-ink-3">{components.length} subsystems</span>}>Subsystem status</SectionTitle>
                <div className="grid grid-cols-2 gap-1.5 md:grid-cols-3">
                  {components.map((c) => (
                    <button key={c.id} type="button" onClick={() => goTo(c.id)} className={cn('flex items-center gap-2 rounded-control border px-2 py-1.5 text-left hover:bg-overlay', c.colour === 'red' ? 'border-warning/40' : c.colour === 'yellow' ? 'border-caution/40' : 'border-hairline')} title={c.path}>
                      <StatusIcon colour={c.colour} />
                      <span className="min-w-0 flex-1 truncate text-ink-2">{c.label}</span>
                      <span className={cn('font-mono text-[11px] tabular', c.colour === 'red' ? 'text-warning' : c.colour === 'yellow' ? 'text-caution' : 'text-nominal')}>{c.score}%</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <SectionTitle right={<span className="font-mono text-[10px] text-ink-3">{issues.length}</span>}>Worst issues</SectionTitle>
                  {issues.length === 0 ? (
                    <EmptyState title="No readings outside their limits." />
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {issues.slice(0, 6).map((i) => (
                        <li key={`${i.subsystemId}.${i.dataId}`}>
                          <button type="button" onClick={() => goTo(i.subsystemId)} className="flex w-full items-center gap-2 rounded-control border border-hairline px-2 py-1 text-left hover:bg-overlay">
                            <StatusIcon colour={i.colour} />
                            <span className="min-w-0 flex-1 truncate text-ink">
                              {i.subsystemLabel}, {i.label}
                            </span>
                            <span className="font-mono text-ink-2">{i.value}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <SectionTitle>Maintenance summary</SectionTitle>
                  {history && (
                    <div className="grid grid-cols-2 gap-2">
                      <Stat label="Events" value={history.total} />
                      <Stat label="Replacements" value={history.replacements} />
                      <Stat label="Avg MTBF" value={history.avgMtbfDays !== null ? `${history.avgMtbfDays} d` : 'N/A'} />
                      <Stat label="Last" value={history.lastMs !== null ? fmtDate(history.lastMs) : 'N/A'} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
